import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, BOOKING_URL } from "../../data/site";
import { Sparkle } from "./Sparkle";
import { EASE } from "./Reveal";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const dark = !scrolled && !open;

  return (
    <>
      <header
        data-testid="main-navigation"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-ivory/90 backdrop-blur-md border-b border-stone shadow-[0_10px_40px_rgba(28,27,25,0.06)]"
            : "bg-transparent border-b border-ivory/10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo-link" aria-label="S & Z Hair and Laser — Home" className="shrink-0">
            <img
              src={dark ? "/assets/logo-light.png" : "/assets/logo.png"}
              alt="S & Z Hair and Laser"
              className="h-11 w-auto transition-opacity duration-500"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `font-sans uppercase tracking-[0.18em] text-[11px] font-medium transition-colors duration-300 ${
                    dark
                      ? isActive
                        ? "text-champagne"
                        : "text-ivory/80 hover:text-champagne"
                      : isActive
                      ? "text-gold"
                      : "text-deep/70 hover:text-gold"
                  }`
                }
              >
                {l.name}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-experience-button"
              aria-label="Book an appointment (opens in a new tab)"
              className={`hidden sm:inline-block font-sans uppercase tracking-[0.2em] text-[11px] font-medium px-6 py-2.5 border transition-all duration-300 ${
                dark
                  ? "border-champagne/70 text-champagne hover:bg-gold hover:border-gold hover:text-espresso"
                  : "border-gold text-gold hover:bg-gold hover:text-espresso"
              }`}
            >
              Book Your Experience
            </a>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close menu" : "Open menu"}
              className={`lg:hidden p-2 ${dark ? "text-ivory" : "text-espresso"}`}
            >
              {open ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-40 bg-espresso flex flex-col justify-center px-10"
          >
            <div className="flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.path}
                    data-testid={`mobile-nav-link-${l.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `font-display text-4xl sm:text-5xl py-2 block transition-colors ${
                        isActive ? "text-gold italic" : "text-ivory hover:text-champagne"
                      }`
                    }
                  >
                    {l.name}
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-12 flex items-center gap-3 text-champagne/70"
            >
              <Sparkle size={12} />
              <span className="font-sans uppercase tracking-[0.22em] text-[11px]">
                Brampton, Ontario — By Appointment Only
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
